import * as m from '@staiter/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { CalendarCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { EmptyState } from '~/components/data';
import { ScopeAllCompaniesCta } from '~/components/domain/ScopeAllCompaniesCta';
import { emitToast } from '~/components/feedback/toast-bus';
import { formatDate } from '~/lib/format';
import { createTask, ensureInboxProject } from '~/lib/tasks/api/tasks';
import { businessTodayIso } from '~/lib/tasks/business-day';
import { revalidateListsNow } from '~/lib/tasks/mutations/schedule-list-revalidate';
import { useTasksSearch } from '~/lib/tasks/queries/useTasksSearch';
import { isTaskOverdue } from '~/lib/tasks/sla';
import styles from './_auth.my-day.module.css';

/**
 * task-quick-capture F4 — «Мой день»: primary-срез задач, релевантных сегодня
 * (срок ≤ сегодня OR назначено мне, активные) — backend `view=my_day` (B4).
 * Собственный inline quick-add ставит задачу на сегодня во «Входящие».
 */
export const Route = createFileRoute('/_auth/my-day')({
  component: MyDayPage,
});

function MyDayPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const search = useTasksSearch({
    filter: { view: 'my_day' },
    sortBy: 'due_date',
    limit: 100,
  });
  // Плоский список из групп (group_by не задан → одна группа).
  const tasks = search.data?.pages.flatMap((p) => p.groups.flatMap((g) => g.tasks)) ?? [];
  const projectDir = search.data?.pages[0]?.project_directory ?? [];
  // F94 (§9.5): «просрочено» — предикат `slaInfo` (группа статуса, папка) на деловом дне,
  // как у сервера (`view=my_day`), а не «срок раньше локального дня и не завершена».
  const stateGroupById = new Map(
    (search.data?.pages[0]?.state_directory ?? []).map((s) => [s.state_id, s.group] as const),
  );
  const today = businessTodayIso();
  const projectName = (id: string): string =>
    projectDir.find((p) => p.project_id === id)?.name ?? m.nav_work_tasks();

  const [quickTitle, setQuickTitle] = useState('');
  const quickRef = useRef<HTMLInputElement>(null);
  const tasksEmpty = !search.isLoading && tasks.length === 0;
  useEffect(() => {
    if (tasksEmpty) quickRef.current?.focus();
  }, [tasksEmpty]);

  const quickAdd = useMutation({
    mutationFn: async (title: string) => {
      const p = await ensureInboxProject();
      await createTask({ project_id: p.id, title, kind: 'task', due_date: businessTodayIso() });
      return p.id;
    },
    onSuccess: () => {
      setQuickTitle('');
      emitToast({ variant: 'success', title: m.quickadd_created_toast() });
      // work-tasks-performance F10 — точечно; см. QuickAddTask.
      revalidateListsNow(queryClient);
      // Фокус остаётся в поле — Things-паттерн «строчить подряд».
      quickRef.current?.focus();
    },
  });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{m.myday_title()}</h1>
        {/* tasks-by-company-fixes F-1 — паритет: scope-CTA и здесь. */}
        <ScopeAllCompaniesCta />
      </header>

      {/* Quick-add «на сегодня» — всегда доступен сверху (не только в empty). */}
      <input
        ref={quickRef}
        type="text"
        className={styles.quickInput}
        placeholder={m.myday_quickadd_placeholder()}
        value={quickTitle}
        disabled={quickAdd.isPending}
        onChange={(e) => setQuickTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && quickTitle.trim()) {
            e.preventDefault();
            quickAdd.mutate(quickTitle.trim());
          } else if (e.key === 'Escape') {
            (e.target as HTMLInputElement).blur();
          }
        }}
        aria-label={m.myday_quickadd_placeholder()}
      />

      {tasksEmpty ? (
        <EmptyState
          icon={<CalendarCheck size={28} aria-hidden="true" />}
          title={m.myday_empty_title()}
          description={m.myday_empty_hint()}
        />
      ) : (
        <ul className={styles.list}>
          {tasks.map((t) => {
            const overdue = isTaskOverdue(t, stateGroupById.get(t.state_id), today);
            return (
              <li key={t.id} className={styles.row}>
                <Link
                  to="/work-tasks/$projectId/task/$taskId"
                  params={{ projectId: t.project_id, taskId: t.id }}
                  search={{ mode: 'drawer' }}
                  className={styles.rowTitle}
                >
                  {t.title}
                </Link>
                <span className={styles.rowProject}>{projectName(t.project_id)}</span>
                {t.due_date != null && (
                  <span className={[styles.rowDue, overdue ? styles.rowDueOverdue : ''].join(' ')}>
                    {formatDate(t.due_date)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
